import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download, Loader2, ArrowLeft, Sparkles, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIN_FIELDS = [
  ["capacity_kwp", "Capacity (kWp)", "number", "Plant DC capacity"],
  ["cost_per_kwp", "Price per kWp (₹)", "number", "All-in EPC price per kWp"],
  ["total_project_cost", "Total project cost (₹)", "number", "Auto = capacity × price per kWp; overwrite if the quote differs"],
  ["debt_ratio", "Debt ratio (0–1)", "number", "Share of cost funded by the bank loan, e.g. 0.7"],
  ["interest_rate", "Interest rate (% p.a.)", "number", "Bank lending rate"],
  ["loan_tenure_years", "Loan tenure (years)", "number", "Repayment period"],
  ["tariff", "Grid tariff offset (₹/kWh)", "number", "What the borrower pays the DISCOM today — every solar unit saves this much"],
  ["specific_yield", "Specific yield (kWh/kWp/yr)", "number", "Annual generation per kWp, ~1,400–1,700 for most of India"],
  ["om_per_kwp_year", "O&M cost (₹/kWp/year)", "number", "Annual operation & maintenance, typically ₹400–800 per kWp"],
];
const TECH_FIELDS = [
  ["module_brand", "Module Brand", "text"],
  ["module_model", "Module Model", "text"],
  ["module_wp", "Module Wattage (Wp)", "number"],
  ["inverter_brand", "Inverter Brand", "text"],
  ["inverter_model", "Inverter Model", "text"],
  ["mounting_type", "Mounting Structure", "text"],
];

const STATUS_STYLE = {
  draft: "bg-[#F1F5F9] text-[#475569]", generating: "bg-[#FEF3C7] text-[#92400E]",
  ready: "bg-[#DCFCE7] text-[#166534]", failed: "bg-[#FEE2E2] text-[#B91C1C]",
};

export default function BankReportPage() {
  const { id: projectId, leadId } = useParams();
  const navigate = useNavigate();
  const srcQuery = leadId ? `lead_id=${leadId}` : `project_id=${projectId}`;
  const [reports, setReports] = useState([]);
  const [title, setTitle] = useState("");
  const [borrower, setBorrower] = useState("");
  const [inputs, setInputs] = useState({ debt_ratio: 0.7, interest_rate: 10.5, loan_tenure_years: 10, tariff: 7.5, specific_yield: 1500, om_per_kwp_year: 600, cost_per_kwp: 45000 });
  const [costTouched, setCostTouched] = useState(false);   // total cost auto-fills until the user overwrites it
  const [current, setCurrent] = useState(null); // active report id for attachments
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState({});          // report id → true between the click and the server flipping it to "generating"
  const [llmStatus, setLlmStatus] = useState(null);
  const [now, setNow] = useState(Date.now());
  const { user } = useAuth();
  const canEditReports = can(user, "bank_report.edit");   // create / generate / delete; others only download

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bank-reports?${srcQuery}`);
      setReports(data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Your role cannot view bank reports");
      setReports([]);
    }
  }, [srcQuery]);

  useEffect(() => { load(); }, [load]);

  // Seed capacity from the project so the total cost can be worked out straight away
  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/projects/${projectId}`).then(({ data }) => {
      if (data?.capacity_kwp) setInputs(p => {
        const n = { ...p, capacity_kwp: p.capacity_kwp || data.capacity_kwp };
        const cap = parseFloat(n.capacity_kwp) || 0, ppk = parseFloat(n.cost_per_kwp) || 0;
        if (!n.total_project_cost && cap > 0 && ppk > 0) n.total_project_cost = Math.round(cap * ppk);
        return n;
      });
      if (data?.client_name) setBorrower(b => b || data.client_name);
      if (data?.name) setTitle(t => t || `DPR — ${data.name}`);
    }).catch(() => {});
  }, [projectId]);

  // poll while any report is generating; tick a clock so the row shows how long it has been running
  useEffect(() => {
    if (reports.some(r => r.status === "generating")) {
      const t = setTimeout(load, 4000);
      const tick = setInterval(() => setNow(Date.now()), 1000);
      return () => { clearTimeout(t); clearInterval(tick); };
    }
  }, [reports, load]);
  useEffect(() => {
    axios.get(`${API}/bank-reports/llm-status`).then(({ data }) => setLlmStatus(data)).catch(() => setLlmStatus(null));
  }, []);
  const elapsed = (r) => { const t = Date.parse(r.updated_at || ""); return Number.isNaN(t) ? null : Math.max(0, Math.round((now - t) / 1000)); };

  const setField = (k, v) => {
    if (k === "total_project_cost") setCostTouched(v !== "");
    setInputs(p => {
      const n = { ...p, [k]: v };
      if (!costTouched && (k === "capacity_kwp" || k === "cost_per_kwp")) {
        const cap = parseFloat(n.capacity_kwp) || 0, ppk = parseFloat(n.cost_per_kwp) || 0;
        n.total_project_cost = cap > 0 && ppk > 0 ? Math.round(cap * ppk) : "";
      }
      return n;
    });
  };

  const createReport = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bank-reports`, { ...(leadId ? { lead_id: leadId } : { project_id: projectId }), title, borrower_name: borrower, inputs });
      setCurrent(data.id);
      await load();
      toast.success("Draft created — add attachments, then generate");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    setBusy(false);
  };

  const uploadAttachment = async (reportId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/bank-reports/${reportId}/attachments`, fd);
      await load();
      toast.success("Attachment added");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const removeAttachment = async (reportId, attId) => {
    await axios.delete(`${API}/bank-reports/${reportId}/attachments/${attId}`);
    await load();
  };

  const generate = async (reportId) => {
    if (starting[reportId]) return;
    setStarting(p => ({ ...p, [reportId]: true }));                       // immediate feedback — before the server answers
    setReports(p => p.map(r => (r.id === reportId ? { ...r, status: "generating", error: null, updated_at: new Date().toISOString() } : r)));
    try {
      await axios.post(`${API}/bank-reports/${reportId}/generate`);
      await load();
      toast.info(`Generating your bankable report${llmStatus?.enabled ? ` — narratives by ${llmStatus.model}` : ""}… usually 15–60 s`);
    } catch (e) {
      await load();
      toast.error(errText(e, "Failed to start generation"));
    } finally { setStarting(p => { const n = { ...p }; delete n[reportId]; return n; }); }
  };
  const errText = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (e?.response?.status) return `${fallback} (server replied ${e.response.status})`;
    return e?.message ? `${fallback} — ${e.message}` : fallback;
  };

  const del = async (reportId) => {
    const r = reports.find(x => x.id === reportId);
    if (!window.confirm(`Delete report "${r?.title || "this report"}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/bank-reports/${reportId}`);
      await load();
      toast.success("Report deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete the report"); }
  };

  const activeReport = reports.find(r => r.id === current);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="bank-report-page">
      <button onClick={() => navigate(leadId ? `/app/crm` : `/app/projects/${projectId}`)} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#0A1128]">
        <ArrowLeft size={15} /> {leadId ? "Back to CRM" : "Back to project"}
      </button>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#FFB703]/15 flex items-center justify-center"><FileText size={22} className="text-[#FFB703]" /></div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#0A1128]">Bankable Project Report</h1>
          <p className="text-sm text-[#64748B]">Generate a detailed 15-20 page DPR for banks / loan submissions.</p>
        </div>
      </div>

      {/* New report form */}
      {canEditReports && <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4">
        <h2 className="font-semibold text-base text-[#0A1128] flex items-center gap-2"><Sparkles size={16} className="text-[#2F7D52]" /> New Report Inputs</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-[#64748B] uppercase">Report Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="DPR — 250 kWp Rooftop" data-testid="dpr-title-input"
              className="w-full mt-1 border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#64748B] uppercase">Borrower / Promoter</label>
            <input value={borrower} onChange={e => setBorrower(e.target.value)} placeholder="Acme Industries Pvt Ltd" data-testid="dpr-borrower-input"
              className="w-full mt-1 border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        <div>
          <div className="text-xs font-bold text-[#2F7D52] uppercase tracking-wide mb-2">Financial Assumptions</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {FIN_FIELDS.map(([k, label, type, hint]) => (
              <div key={k}>
                <label className="text-[11px] text-[#64748B]">{label}</label>
                <input type={type} value={inputs[k] ?? ""} onChange={e => setField(k, e.target.value === "" ? "" : (type === "number" ? parseFloat(e.target.value) : e.target.value))}
                  data-testid={`dpr-input-${k}`} className={`w-full mt-1 border rounded-lg px-2.5 py-1.5 text-sm ${k === "total_project_cost" && !costTouched && inputs.total_project_cost ? "border-[#2F7D52]/40 bg-[#F5FAF7]" : "border-[#E2E8F0]"}`} />
                {hint && <p className="text-[10px] text-[#94A3B8] mt-0.5 leading-tight">{k === "total_project_cost" && !costTouched && inputs.total_project_cost ? `Auto: ${inputs.capacity_kwp} kWp × ₹${Number(inputs.cost_per_kwp).toLocaleString("en-IN")}` : hint}</p>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-bold text-[#2F7D52] uppercase tracking-wide mb-2">Technical Details</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {TECH_FIELDS.map(([k, label, type]) => (
              <div key={k}>
                <label className="text-[11px] text-[#64748B]">{label}</label>
                <input type={type} value={inputs[k] ?? ""} onChange={e => setField(k, type === "number" && e.target.value !== "" ? parseFloat(e.target.value) : e.target.value)}
                  data-testid={`dpr-input-${k}`} className="w-full mt-1 border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 text-sm" />
              </div>
            ))}
          </div>
        </div>

        <button onClick={createReport} disabled={busy} data-testid="dpr-create-btn"
          className="bg-[#2F7D52] text-white font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-[#25623F] disabled:opacity-60 flex items-center gap-2">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} Create Draft
        </button>
      </div>}

      {/* Attachments for the active/newest draft */}
      {activeReport && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="dpr-attachments">
          <h2 className="font-semibold text-sm text-[#0A1128]">Annexures for “{activeReport.title}”</h2>
          <p className="text-xs text-[#64748B]">Upload datasheets, quotations, layout, land docs, etc. They're appended to the final PDF.</p>
          <div className="space-y-2">
            {(activeReport.attachments || []).map(a => (
              <div key={a.id} className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2">
                <span className="text-sm text-[#0A1128] truncate flex items-center gap-2"><Building2 size={13} className="text-[#94A3B8]" />{a.name}</span>
                {canEditReports && <button onClick={() => removeAttachment(activeReport.id, a.id)} className="text-[#DC2626] hover:bg-[#FEF2F2] rounded p-1"><Trash2 size={14} /></button>}
              </div>
            ))}
          </div>
          {canEditReports && <label className="inline-flex items-center gap-2 text-sm text-[#2F7D52] cursor-pointer border border-dashed border-[#2F7D52]/40 rounded-lg px-3 py-2 hover:bg-[#2F7D52]/5" data-testid="dpr-upload-label">
            <Upload size={14} /> Add attachment
            <input type="file" className="hidden" onChange={e => { if (e.target.files[0]) uploadAttachment(activeReport.id, e.target.files[0]); e.target.value = ""; }} />
          </label>}
        </div>
      )}

      {/* Reports list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-semibold text-base text-[#0A1128]">Generated Reports</h2>
          {llmStatus && (llmStatus.enabled
            ? <span className="text-[11px] text-[#475569] bg-[#F1F5F9] px-2 py-1 rounded-full" data-testid="dpr-llm-status">Narratives by Claude · {llmStatus.model} (Anthropic)</span>
            : <span className="text-[11px] text-[#92400E] bg-[#FEF3C7] px-2 py-1 rounded-full" data-testid="dpr-llm-status" title={llmStatus.note || ""}>AI narratives off — template text will be used</span>)}
        </div>
        {reports.length === 0 && <p className="text-sm text-[#94A3B8]">No reports yet.{canEditReports ? " Fill the inputs above and create your first draft." : ""}</p>}
        {reports.map(r => (
          <div key={r.id} data-testid={`dpr-report-${r.id}`} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-center justify-between gap-3">
            <div className="min-w-0" onClick={() => setCurrent(r.id)} style={{ cursor: "pointer" }}>
              <div className="font-semibold text-sm text-[#0A1128] truncate">{r.title}</div>
              <div className="text-xs text-[#64748B]">{r.borrower_name} · {(r.attachments || []).length} annexure(s)
                {r.metrics?.irr_project ? ` · IRR ${r.metrics.irr_project.toFixed(1)}%` : ""}</div>
              {r.status === "failed" && <div className="text-[11px] text-[#B91C1C] mt-0.5">{r.error}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${STATUS_STYLE[r.status] || STATUS_STYLE.draft}`} data-testid={`dpr-status-${r.id}`}>
                {starting[r.id] ? "Starting…" : r.status === "generating" ? `Generating…${elapsed(r) != null ? ` ${elapsed(r)} s` : ""}` : r.status.toUpperCase()}
              </span>
              {r.status === "ready" && (
                <a href={`${API}/bank-reports/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`dpr-download-${r.id}`}
                  className="flex items-center gap-1 bg-[#2F7D52] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#25623F]"><Download size={13} /> Save PDF</a>
              )}
              {canEditReports && r.status !== "generating" && (
                <button onClick={() => generate(r.id)} data-testid={`dpr-generate-${r.id}`} disabled={!!starting[r.id]}
                  className="flex items-center gap-1 bg-[#FFB703] text-[#0A1128] text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#E8A200] disabled:opacity-60">
                  {starting[r.id] ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {r.status === "ready" ? "Regenerate" : "Generate"}
                </button>
              )}
              {r.status === "generating" && <span className="flex items-center gap-1 text-[11px] text-[#92400E]" data-testid={`dpr-spinner-${r.id}`}><Loader2 size={16} className="animate-spin" /> building PDF</span>}
              {canEditReports && <button onClick={() => del(r.id)} className="text-[#DC2626] hover:bg-[#FEF2F2] rounded p-1.5" title="Delete report"><Trash2 size={14} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
